import React, { useEffect, useState } from 'react';
import { crmApi } from '../api/crm';
import { useToast } from '../hooks/useToast';

const STAGES: [string,string][] = [
  ['', 'All'], ['prospect','Prospect'], ['visited','Visited'],
  ['sampling','Sampling'], ['ordering','Ordering'], ['dormant','Dormant'],
];
const STAGE_COLOUR: any = {
  prospect:'badge-blue', visited:'badge-amber', sampling:'badge-purple',
  ordering:'badge-green', dormant:'badge-red',
};

const Accounts: React.FC = () => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<any[]>([]);
  const [stage, setStage] = useState('');
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<any>(null);
  const [visit, setVisit] = useState<any>(null);
  const [person, setPerson] = useState<any>(null);

  const today = new Date().toISOString().split('T')[0];
  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  const load = () => {
    setLoading(true);
    crmApi.accounts({ stage: stage || undefined, mine: mine ? 1 : undefined, search: search || undefined })
      .then(r => setRows(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [stage, mine]); // eslint-disable-line

  const openOne = async (id:number) => {
    try { const r = await crmApi.getAccount(id); setOpen(r.data.data); }
    catch { showToast('Could not open the account', 'error'); }
  };

  const saveVisit = async () => {
    if (!visit.note.trim()) { showToast('Write what was discussed', 'error'); return; }
    try {
      await crmApi.followUp({
        contact_id: open.id,
        contact_person_id: visit.contact_person_id ? Number(visit.contact_person_id) : null,
        action_date: visit.action_date, channel: visit.channel, note: visit.note,
        outcome: visit.outcome || null,
        stage_after: visit.stage_after || null,
        next_action_date: visit.next_action_date || null,
      });
      showToast('Visit recorded');
      setVisit(null); openOne(open.id); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
  };

  const savePerson = async () => {
    if (!person.name.trim()) { showToast('Name is required', 'error'); return; }
    try {
      await crmApi.addPerson({ ...person, contact_id: open.id, is_primary: !!person.is_primary });
      showToast('Person added');
      setPerson(null); openOne(open.id);
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
  };

  const removePerson = async (id:number, name:string) => {
    if (!window.confirm('Remove ' + name + ' from this account?')) return;
    try { await crmApi.removePerson(id); showToast('Removed'); openOne(open.id); }
    catch { showToast('Could not remove', 'error'); }
  };

  const setStageOn = async (s:string) => {
    try { await crmApi.updateAccount(open.id, { crm_stage: s }); showToast('Stage set to ' + s); openOne(open.id); load(); }
    catch { showToast('Could not update', 'error'); }
  };

  const th: React.CSSProperties = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' };

  // ---------- log a visit ----------
  if (visit) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Log visit — {open.name}</h1>
        <button className="btn btn-secondary" onClick={() => setVisit(null)}>Cancel</button>
      </div>
      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Date</label>
            <input type="date" className="form-control" value={visit.action_date} onChange={e => setVisit({...visit, action_date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">How</label>
            <select className="form-control" value={visit.channel} onChange={e => setVisit({...visit, channel:e.target.value})}>
              <option value="visit">Visit</option><option value="call">Call</option>
              <option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="other">Other</option>
            </select></div>
          <div className="form-group"><label className="form-label">Who did you see?</label>
            <select className="form-control" value={visit.contact_person_id} onChange={e => setVisit({...visit, contact_person_id:e.target.value})}>
              <option value="">Not recorded</option>
              {(open.people || []).map((p:any) => <option key={p.id} value={p.id}>{p.name}{p.role ? ' — ' + p.role : ''}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Move to stage</label>
            <select className="form-control" value={visit.stage_after} onChange={e => setVisit({...visit, stage_after:e.target.value})}>
              <option value="">Leave as {open.crm_stage || 'not set'}</option>
              {STAGES.filter(s => s[0]).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
        </div>
        <div className="form-group"><label className="form-label">What was discussed *</label>
          <textarea className="form-control" rows={3} value={visit.note} onChange={e => setVisit({...visit, note:e.target.value})} autoFocus /></div>
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Outcome</label>
            <input className="form-control" placeholder="e.g. asked for samples, will order next week" value={visit.outcome} onChange={e => setVisit({...visit, outcome:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Next visit</label>
            <input type="date" className="form-control" value={visit.next_action_date} onChange={e => setVisit({...visit, next_action_date:e.target.value})} /></div>
        </div>
        <button className="btn btn-primary" onClick={saveVisit}>Save visit</button>
      </div></div>
    </div>
  );

  // ---------- add a person ----------
  if (person) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Add person — {open.name}</h1>
        <button className="btn btn-secondary" onClick={() => setPerson(null)}>Cancel</button>
      </div>
      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Name *</label>
            <input className="form-control" value={person.name} onChange={e => setPerson({...person, name:e.target.value})} autoFocus /></div>
          <div className="form-group"><label className="form-label">Role</label>
            <input className="form-control" placeholder="Pharmacist, Purchasing, Owner..." value={person.role} onChange={e => setPerson({...person, role:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Mobile</label>
            <input className="form-control mono" value={person.mobile} onChange={e => setPerson({...person, mobile:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Email</label>
            <input className="form-control" value={person.email} onChange={e => setPerson({...person, email:e.target.value})} /></div>
        </div>
        <div className="form-group">
          <label style={{ fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
            <input type="checkbox" checked={!!person.is_primary} onChange={e => setPerson({...person, is_primary:e.target.checked})} />
            Main contact at this account
          </label>
        </div>
        <button className="btn btn-primary" onClick={savePerson}>Save</button>
      </div></div>
    </div>
  );

  // ---------- one account ----------
  if (open) return (
    <div>
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => { setOpen(null); load(); }}>Back</button>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setPerson({ name:'', role:'', mobile:'', email:'', is_primary:false })}>+ Person</button>
          <button className="btn btn-primary" onClick={() => setVisit({ action_date:today, channel:'visit', note:'', outcome:'', stage_after:'', next_action_date:'', contact_person_id:'' })}>Log visit</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom:12 }}>
        <div className="card-header">
          <span>{open.name}</span>
          <span className={'badge ' + (STAGE_COLOUR[open.crm_stage] || 'badge-blue')}>{open.crm_stage || 'not set'}</span>
        </div>
        <div className="card-body">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:10, fontSize:13 }}>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Mobile</div>{open.mobile || '-'}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Address</div>{open.address || '-'}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Rep</div>{open.assigned_name || 'unassigned'}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Last visit</div>{open.last_visit_date || 'never'}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Next visit</div>
              <span style={{ color: open.next_visit_date && open.next_visit_date < today ? 'var(--red)' : undefined }}>{open.next_visit_date || '-'}</span></div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Owes</div>
              <span style={{ color: Number(open.ar_balance) > 0 ? 'var(--red)' : undefined }}>{fmt(open.ar_balance)}</span></div>
          </div>
          <div style={{ marginTop:12, paddingTop:10, borderTop:'1px solid var(--bdr)', display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ fontSize:11, color:'var(--tx3)' }}>Set stage:</span>
            {STAGES.filter(s => s[0]).map(([v,l]) => (
              <button key={v} className={'btn btn-sm ' + (open.crm_stage===v ? 'btn-primary' : 'btn-secondary')} onClick={() => setStageOn(v)}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom:12 }}>
        <div className="card-header"><span>People</span><span className="record-count">{open.people?.length || 0}</span></div>
        {(open.people || []).length === 0
          ? <div className="empty-state"><p>Nobody recorded yet.</p></div>
          : <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={th}>Name</th><th style={th}>Role</th><th style={th}>Mobile</th><th style={th}>Email</th><th></th>
              </tr></thead>
              <tbody>
                {open.people.map((p:any) => (
                  <tr key={p.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'8px 10px', fontSize:12, fontWeight: p.is_primary ? 600 : 400 }}>
                      {p.name}{p.is_primary ? ' · main' : ''}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{p.role || '-'}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, fontFamily:'monospace' }}>{p.mobile || '-'}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{p.email || '-'}</td>
                    <td style={{ padding:'6px 10px', textAlign:'right' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => removePerson(p.id, p.name)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>

      <div className="card">
        <div className="card-header"><span>Visit history</span><span className="record-count">{open.follow_ups?.length || 0}</span></div>
        {(open.follow_ups || []).length === 0
          ? <div className="empty-state"><p>No visits recorded yet.</p></div>
          : <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={th}>Date</th><th style={th}>How</th><th style={th}>Met</th><th style={th}>Note</th>
                <th style={th}>Outcome</th><th style={th}>Next</th><th style={th}>By</th>
              </tr></thead>
              <tbody>
                {open.follow_ups.map((f:any) => (
                  <tr key={f.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{f.action_date}</td>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{f.channel}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{f.person_name || '-'}</td>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{f.note}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{f.outcome || '-'}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{f.next_action_date || '-'}</td>
                    <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{f.by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
    </div>
  );

  // ---------- the list ----------
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Accounts</h1></div>
      <div style={{ marginBottom:12, display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
        {STAGES.map(([v,l]) => (
          <button key={v} className={'btn btn-sm ' + (stage===v ? 'btn-primary' : 'btn-secondary')} onClick={() => setStage(v)}>{l}</button>
        ))}
        <label style={{ fontSize:12, marginLeft:10, display:'flex', alignItems:'center', gap:6 }}>
          <input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} /> My accounts
        </label>
      </div>
      <div className="filter-bar" style={{ marginBottom:12, display:'flex', gap:8 }}>
        <input className="form-control" style={{ maxWidth:300 }} placeholder="Search name or mobile..."
          value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key==='Enter') load(); }} />
        <button className="btn btn-secondary btn-sm" onClick={load}>Search</button>
        <span className="record-count" style={{ marginLeft:'auto' }}>{rows.length}</span>
      </div>
      <div className="card">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>Account</th><th style={th}>Mobile</th><th style={th}>Stage</th>
            <th style={th}>Last visit</th><th style={th}>Next visit</th>
            <th style={{...th, textAlign:'right'}}>Owes</th><th style={th}>Rep</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>Loading...</td></tr>
            : rows.length === 0 ? <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>No accounts.</td></tr>
            : rows.map(r => (
              <tr key={r.id} onClick={() => openOne(r.id)} style={{ borderBottom:'1px solid var(--bdr)', cursor:'pointer' }}>
                <td style={{ padding:'8px 10px', fontSize:12, fontWeight:600 }}>{r.name}</td>
                <td style={{ padding:'8px 10px', fontSize:12, fontFamily:'monospace' }}>{r.mobile || '-'}</td>
                <td style={{ padding:'8px 10px' }}>
                  <span className={'badge ' + (STAGE_COLOUR[r.crm_stage] || 'badge-blue')}>{r.crm_stage || 'not set'}</span></td>
                <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{r.last_visit_date || 'never'}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color: r.overdue ? 'var(--red)' : 'var(--tx3)' }}>
                  {r.next_visit_date || '-'}{r.overdue ? ' · due' : ''}</td>
                <td className="td-num" style={{ padding:'8px 10px', color: Number(r.ar_balance) > 0 ? 'var(--red)' : 'var(--tx3)' }}>
                  {Number(r.ar_balance) ? fmt(r.ar_balance) : '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{r.assigned_name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Accounts;
